import {
  CreateDataRoomShareableLinkForm,
  DATA_ROOM_SHAREABLE_LINK_ACCESS,
  DATA_ROOM_SHAREABLE_LINK_DESTINATION,
  DATA_ROOM_SHAREABLE_LINK_EXPIRES,
  DataRoomShareableLink,
} from 'models/ShareableLink';
import { DataRoomNda } from 'router/subrouters/DataRoom/models/DataRoomNDA';
import {
  getIsLinkExpiresOther,
  getIsAccessAddPasword,
  getIsAccessAnyone,
  getIsAccessAddSpecificRecipiens,
} from 'router/subrouters/DataRoom/utils';
import { getArrayOfEmails } from 'utils';
import validations from 'validations';

export const createWatermarkParamsForPreview = ({
  watermarkDiagonal,
  watermarkIpAddress,
  watermarkHeaderAndFooter,
  watermarkNameAndEmail,
}: {
  watermarkDiagonal: boolean;
  watermarkIpAddress: boolean;
  watermarkHeaderAndFooter: boolean;
  watermarkNameAndEmail: boolean;
}) => ({
  watermarkDiagonal,
  watermarkIpAddress,
  watermarkHeader: watermarkHeaderAndFooter,
  watermarkFooter: watermarkHeaderAndFooter,
  watermarkFullName: watermarkNameAndEmail,
  watermarkEmail: watermarkNameAndEmail,
});

export const createInitialValues = (termsAndConditionsContent?: string) => ({
  linkAccess: DATA_ROOM_SHAREABLE_LINK_ACCESS.ANYONE_WITH_LINK,
  linkExpires: DATA_ROOM_SHAREABLE_LINK_EXPIRES.SEVEN_DAYS,
  linkDestination: DATA_ROOM_SHAREABLE_LINK_DESTINATION.DIRECT_ACCESS,
  allowDownloads: true,
  includeNdaProtection: false,
  includeTermsAndConditions: false,
  useExistingNda: false,
  recipientEmailAddresses: [] as string[],
  notifyOnSecureLinkAccess: true,
  notifyOnWaitingRoomEntry: true,
  ...(termsAndConditionsContent && {
    includeTermsAndConditionProtection: true,
    termsAndConditionsContent,
  }),
});

export const createInitialValuesFromLink = (
  link: DataRoomShareableLink,
  dataRoomNdas: DataRoomNda[],
  initialTermsAndConditionsContent?: string,
): CreateDataRoomShareableLinkForm => {
  const {
    dataRoomNdaId,
    dataRoomNda,
    linkDestination,
    termsAndConditionsContent,
    ...rest
  } = link;

  const useExistingNda = !!dataRoomNdas?.find(
    ({ _id }) => _id === dataRoomNdaId,
  );

  const nDataRoomNda = dataRoomNda
    ? { ...dataRoomNda.file, _id: dataRoomNda?._id }
    : null;

  return {
    ...rest,
    useExistingNda,
    linkDestination:
      linkDestination || DATA_ROOM_SHAREABLE_LINK_DESTINATION.DIRECT_ACCESS,
    termsAndConditionsContent:
      termsAndConditionsContent || initialTermsAndConditionsContent,
    ...(useExistingNda ? { dataRoomNdaId } : { dataRoomNda: nDataRoomNda }),
  };
};

export const getPayloadFromFormValues = ({
  name,
  linkAccess,
  password,
  recipientEmailAddresses,
  optionalNote,
  anyoneWithNonPublicDomain,
  emailDomain,
  linkExpires,
  linkExpiresDate,
  allowDownloads,
  includeNdaProtection,
  useExistingNda,
  dataRoomNdaId,
  dataRoomNda,
  linkDestination,
  includeTermsAndConditionProtection,
  termsAndConditionsContent,
  notifyOnSecureLinkAccess,
  notifyOnWaitingRoomEntry,
}: CreateDataRoomShareableLinkForm) => ({
  name,
  linkAccess,
  linkExpires,
  allowDownloads,
  includeNdaProtection,
  includeTermsAndConditionProtection,
  notifyOnSecureLinkAccess,
  notifyOnWaitingRoomEntry,
  ...(includeTermsAndConditionProtection && { termsAndConditionsContent }),
  ...(includeNdaProtection && useExistingNda && { dataRoomNdaId }),
  ...(includeNdaProtection &&
    !useExistingNda && { dataRoomNdaId: dataRoomNda?._id }),
  ...(getIsLinkExpiresOther(linkExpires) && { linkExpiresDate }),
  ...(getIsAccessAddPasword(linkAccess) && { password }),
  ...(getIsAccessAnyone(linkAccess) && { recipientEmailAddresses }),
  ...(getIsAccessAddSpecificRecipiens(linkAccess)
    ? {
        optionalNote,
        recipientEmailAddresses,
        anyoneWithNonPublicDomain,
        ...(anyoneWithNonPublicDomain && { emailDomain }),
      }
    : { linkDestination }),
});

export const validate = (values: CreateDataRoomShareableLinkForm) => {
  const {
    linkAccess,
    anyoneWithNonPublicDomain,
    recipientEmailAddresses,
    recipientEmailAddressesUnsubmited,
    includeNdaProtection,
    useExistingNda,
    dataRoomNda,
    dataRoomNdaId,
  } = values;

  const recipiensLentgh = getArrayOfEmails(
    recipientEmailAddresses,
    recipientEmailAddressesUnsubmited,
  )?.length;

  const areRecipiensRequired =
    getIsAccessAddSpecificRecipiens(linkAccess) &&
    !anyoneWithNonPublicDomain &&
    !recipiensLentgh;

  const isNdaUploadRequired = includeNdaProtection && !useExistingNda;
  const isExistingNdaRequired = includeNdaProtection && useExistingNda;

  return {
    ...(areRecipiensRequired && {
      recipientEmailAddresses: 'required',
    }),
    ...(isNdaUploadRequired && {
      dataRoomNda: validations.required()(dataRoomNda),
    }),
    ...(isExistingNdaRequired && {
      dataRoomNdaId: validations.required()(dataRoomNdaId),
    }),
  };
};
